// ─── Build version check ──────────────────────────────────────
// Detects when the deployed frontend bundle is newer than the
// currently-running tab. Uses webpack's content-hashed bundle
// filenames (e.g. `/static/js/main.abc123.js`) — zero build-time
// config required: every deploy automatically gets a new hash.
//
// Why we need this: the PWA / browser / SW will happily keep a
// designer running yesterday's editor for hours after a deploy.
// They then see "phantom" QA bugs that have already been fixed
// in the live code. This module surfaces "newer version available"
// so a one-click refresh resolves it.

const BUNDLE_RX = /\/static\/js\/[^"'\s]*?\.([a-f0-9]{8,})\.(?:chunk\.)?js/;

// Read the hash of the JS bundle the user is currently running.
// Returns null in dev (where webpack-dev-server doesn't hash names).
export function getCurrentBundleHash() {
  if (typeof document === 'undefined') return null;
  const scripts = Array.from(document.scripts);
  for (const s of scripts) {
    const m = (s.src || '').match(BUNDLE_RX);
    if (m) return m[1];
  }
  return null;
}

// Fetch the freshest /index.html (bypassing every cache layer) and
// extract the hash of its main JS bundle.
export async function fetchLatestBundleHash() {
  try {
    const res = await fetch(`/index.html?_bv=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(BUNDLE_RX);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

// Compare current vs deployed bundle hash. Returns true if the
// deployment has shipped a newer build than the running tab.
export async function isClientOutOfDate() {
  const current = getCurrentBundleHash();
  if (!current) return false; // dev or unable to detect — never warn
  const latest = await fetchLatestBundleHash();
  if (!latest) return false;
  return current !== latest;
}
